import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/marketing/components/ui/popover";
import { useFilterStore } from "@/marketing/stores/useFilterStore";

type PlatformValue = "tiktok" | "instagram";

const PLATFORM_ROWS: { value: PlatformValue; label: string }[] = [
  { value: "tiktok", label: "TikTok" },
  { value: "instagram", label: "Instagram" },
];

function PlatformGlyph({
  platform,
  size = 16,
}: {
  platform: PlatformValue;
  size?: number;
}) {
  if (platform === "tiktok") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden
      >
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.95a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x={2} y={2} width={20} height={20} rx={5} ry={5} />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1={17.5} y1={6.5} x2={17.51} y2={6.5} />
    </svg>
  );
}

function platformLabel(platform: string): string {
  if (platform === "tiktok") return "TikTok";
  if (platform === "instagram") return "Instagram";
  return "Platform";
}

export function PlatformFilter() {
  const platform = useFilterStore((s) => s.filters.platform);
  const setPlatform = useFilterStore((s) => s.setPlatform);
  const [open, setOpen] = useState(false);

  const handleSelect = (value: PlatformValue) => {
    setPlatform(value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="tw-inline-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm tw-transition-colors hover:tw-bg-accent hover:tw-text-accent-foreground focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring"
        >
          Platform: {platformLabel(platform)}
          <ChevronDown className="tw-h-4 tw-w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="tw-w-48 tw-p-1">
        {PLATFORM_ROWS.map((row) => {
          const selected = platform === row.value;
          return (
            <button
              type="button"
              key={row.value}
              onClick={() => handleSelect(row.value)}
              className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-sm tw-px-2 tw-py-2 tw-text-left tw-text-sm hover:tw-bg-accent"
            >
              <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-text-muted-foreground">
                <PlatformGlyph platform={row.value} />
              </span>
              <span className="tw-flex-1">{row.label}</span>
              {selected && <Check className="tw-h-4 tw-w-4 tw-text-primary" />}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
